/**
 * iQIES Export Tab - CMS HOPE Data Submission Management
 * 
 * Features:
 * - Export HOPE assessments to iQIES portal
 * - Batch validation before export
 * - Submission status tracking
 * - Automated scheduler for deadline reminders
 * - CMS webhook integration for real-time status updates
 */

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ClipboardList, CheckCircle, AlertCircle, Clock, FileText,
  Loader2, Activity, Brain, Info, ChevronRight, Calendar,
  FileSignature, MessageCircle, PlayCircle, Copy
} from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "./constants";

const WEBHOOK_EVENTS = [
  { value: "submission_received", label: "Submission Received", description: "CMS has received the submission" },
  { value: "validation_started", label: "Validation Started", description: "CMS has started validating" },
  { value: "validation_complete", label: "Validation Complete", description: "CMS validation is complete" },
  { value: "validation_error", label: "Validation Error", description: "CMS found validation errors" },
  { value: "submission_accepted", label: "Submission Accepted", description: "Submission accepted with confirmation" },
  { value: "submission_rejected", label: "Submission Rejected", description: "Submission rejected with reason" },
  { value: "partial_accept", label: "Partial Accept", description: "Some records accepted, some rejected" }
];

export default function IQIESExportTab({ loading, isDark, onRefresh, token }) {
  const [exportStatus, setExportStatus] = useState(null);
  const [recordsForExport, setRecordsForExport] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [exportFormat, setExportFormat] = useState("xml");
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportedContent, setExportedContent] = useState(null);
  const [submissionTracking, setSubmissionTracking] = useState(null);
  const [schedulerConfig, setSchedulerConfig] = useState(null);
  const [runningScheduler, setRunningScheduler] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("export");
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [selectedTestEvent, setSelectedTestEvent] = useState("submission_accepted");

  useEffect(() => {
    const fetchExportStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/compliance-hub/iqies/export-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setExportStatus(await res.json());
      } catch (error) {
        console.error("Export status fetch error:", error);
      }
    };

    const fetchRecords = async () => {
      try {
        const res = await fetch(`${API_URL}/api/compliance-hub/iqies/records-for-export`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setRecordsForExport(data.records || []);
        }
      } catch (error) {
        console.error("Records fetch error:", error);
      }
    };

    const fetchSubmissionTracking = async () => {
      try {
        const res = await fetch(`${API_URL}/api/compliance-hub/iqies/submission-tracking`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setSubmissionTracking(await res.json());
      } catch (error) {
        console.error("Submission tracking fetch error:", error);
      }
    };

    const fetchSchedulerConfig = async () => {
      try {
        const res = await fetch(`${API_URL}/api/compliance-hub/iqies/scheduler/config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setSchedulerConfig(await res.json());
      } catch (error) {
        console.error("Scheduler config fetch error:", error);
      }
    };

    const fetchWebhookConfig = async () => {
      try {
        const res = await fetch(`${API_URL}/api/compliance-hub/iqies/webhook/config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setWebhookConfig(await res.json());
      } catch (error) {
        console.error("Webhook config fetch error:", error);
      }
    };

    const fetchWebhookEvents = async () => {
      try {
        const res = await fetch(`${API_URL}/api/compliance-hub/iqies/webhook/events`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setWebhookEvents(data.events || []);
        }
      } catch (error) {
        console.error("Webhook events fetch error:", error);
      }
    };

    fetchExportStatus();
    fetchRecords();
    fetchSubmissionTracking();
    fetchSchedulerConfig();
    fetchWebhookConfig();
    fetchWebhookEvents();
  }, [token]);

  const runSchedulerNow = async () => {
    try {
      setRunningScheduler(true);
      const res = await fetch(`${API_URL}/api/compliance-hub/iqies/scheduler/run-now`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
      }
    } catch (error) {
      toast.error("Failed to run scheduler");
    } finally {
      setRunningScheduler(false);
    }
  };

  const testWebhook = async () => {
    try {
      setTestingWebhook(true);
      const res = await fetch(`${API_URL}/api/compliance-hub/iqies/webhook/test?event_type=${selectedTestEvent}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`Test webhook (${selectedTestEvent}) sent successfully!`);
        // Refresh webhook events
        const eventsRes = await fetch(`${API_URL}/api/compliance-hub/iqies/webhook/events`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (eventsRes.ok) {
          const eventsData = await eventsRes.json();
          setWebhookEvents(eventsData.events || []);
        }
      }
    } catch (error) {
      toast.error("Failed to send test webhook");
    } finally {
      setTestingWebhook(false);
    }
  };

  const validateBatch = async () => {
    try {
      setValidating(true);
      const res = await fetch(`${API_URL}/api/compliance-hub/iqies/validate-batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ format: exportFormat })
      });
      
      if (res.ok) {
        const data = await res.json();
        setValidationResult(data);
        if (data.validation_passed) {
          toast.success(`Validation passed! ${data.valid_count} records ready for export.`);
        } else {
          toast.warning(`Validation found ${data.error_count} errors. Review before exporting.`);
        }
      }
    } catch (error) {
      toast.error("Validation failed");
    } finally {
      setValidating(false);
    }
  };

  const exportBatch = async () => {
    try {
      setExporting(true);
      const res = await fetch(`${API_URL}/api/compliance-hub/iqies/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ format: exportFormat })
      });
      
      if (res.ok) {
        const data = await res.json();
        setExportedContent(data);
        setShowExportDialog(true);
        toast.success(`Export generated! Batch ID: ${data.batch_id}`);
      }
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const downloadExport = () => {
    if (!exportedContent) return;
    
    const content = exportFormat === "json" 
      ? JSON.stringify(exportedContent.content, null, 2) 
      : exportedContent.content;
    
    const blob = new Blob([content], { type: exportedContent.content_type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = exportedContent.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("File downloaded!");
  };

  const copyWebhookUrl = () => {
    if (webhookConfig?.webhook_url) {
      navigator.clipboard.writeText(webhookConfig.webhook_url);
      toast.success("Webhook URL copied!");
    }
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs for Export, Status Tracking, Scheduler, Webhook */}
      <div className="flex items-center gap-2 border-b border-slate-700 pb-2">
        <Button 
          variant={activeSubTab === "export" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveSubTab("export")}
          className={activeSubTab === "export" ? "bg-emerald-600" : ""}
        >
          <FileText className="w-4 h-4 mr-2" />
          Export
        </Button>
        <Button 
          variant={activeSubTab === "tracking" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveSubTab("tracking")}
          className={activeSubTab === "tracking" ? "bg-emerald-600" : ""}
        >
          <Activity className="w-4 h-4 mr-2" />
          Status Tracking
        </Button>
        <Button 
          variant={activeSubTab === "scheduler" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveSubTab("scheduler")}
          className={activeSubTab === "scheduler" ? "bg-emerald-600" : ""}
        >
          <Clock className="w-4 h-4 mr-2" />
          Scheduler
        </Button>
        <Button 
          variant={activeSubTab === "webhook" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveSubTab("webhook")}
          className={activeSubTab === "webhook" ? "bg-emerald-600" : ""}
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          Webhook
        </Button>
        <div className="flex-1" />
        {submissionTracking?.stats && (
          <div className="flex items-center gap-4 text-xs">
            <span className="text-green-400">{submissionTracking.stats.accepted} Accepted</span>
            <span className="text-red-400">{submissionTracking.stats.rejected} Rejected</span>
            <span className="text-yellow-400">{submissionTracking.stats.pending} Pending</span>
            <Badge className="bg-blue-500/20 text-blue-400">
              {submissionTracking.stats.acceptance_rate}% Acceptance Rate
            </Badge>
          </div>
        )}
      </div>

      {/* Export Sub-Tab */}
      {activeSubTab === "export" && (
        <ExportSubTab 
          isDark={isDark}
          exportStatus={exportStatus}
          recordsForExport={recordsForExport}
          validationResult={validationResult}
          exporting={exporting}
          validating={validating}
          exportFormat={exportFormat}
          setExportFormat={setExportFormat}
          validateBatch={validateBatch}
          exportBatch={exportBatch}
          showExportDialog={showExportDialog}
          setShowExportDialog={setShowExportDialog}
          exportedContent={exportedContent}
          downloadExport={downloadExport}
        />
      )}

      {/* Status Tracking Sub-Tab */}
      {activeSubTab === "tracking" && (
        <TrackingSubTab isDark={isDark} submissionTracking={submissionTracking} />
      )}

      {/* Scheduler Sub-Tab */}
      {activeSubTab === "scheduler" && (
        <SchedulerSubTab 
          isDark={isDark}
          schedulerConfig={schedulerConfig}
          runningScheduler={runningScheduler}
          runSchedulerNow={runSchedulerNow}
        />
      )}

      {/* Webhook Sub-Tab */}
      {activeSubTab === "webhook" && (
        <WebhookSubTab 
          isDark={isDark}
          webhookConfig={webhookConfig}
          webhookEvents={webhookEvents}
          testingWebhook={testingWebhook}
          selectedTestEvent={selectedTestEvent}
          setSelectedTestEvent={setSelectedTestEvent}
          testWebhook={testWebhook}
          copyWebhookUrl={copyWebhookUrl}
        />
      )}
    </div>
  );
}

// ==================== SUB-COMPONENTS ====================

function ExportSubTab({
  isDark, exportStatus, recordsForExport, validationResult, exporting, validating,
  exportFormat, setExportFormat, validateBatch, exportBatch,
  showExportDialog, setShowExportDialog, exportedContent, downloadExport
}) {
  return (
    <>
      {/* Export Status Summary */}
      <div className="grid grid-cols-4 gap-4">
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-blue-400" />
              <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Ready for Export</span>
            </div>
            <p className="text-2xl font-bold">{exportStatus?.ready_for_export || 0}</p>
            <p className="text-xs text-green-400">Completed & Validated</p>
          </CardContent>
        </Card>

        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Pending Validation</span>
            </div>
            <p className="text-2xl font-bold">{exportStatus?.pending_validation || 0}</p>
            <p className="text-xs text-yellow-400">Needs Review</p>
          </CardContent>
        </Card>

        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Validation Errors</span>
            </div>
            <p className="text-2xl font-bold">{exportStatus?.validation_errors || 0}</p>
            <p className="text-xs text-red-400">Fix Required</p>
          </CardContent>
        </Card>

        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-purple-400" />
              <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Next Deadline</span>
            </div>
            <p className="text-2xl font-bold">{exportStatus?.next_deadline?.days_remaining || 0}</p>
            <p className="text-xs text-purple-400">Days Remaining</p>
          </CardContent>
        </Card>
      </div>

      {/* CMS iQIES Info Box */}
      <Card className={`border-l-4 border-l-blue-500 ${isDark ? "bg-slate-900/50 border-slate-800" : "bg-blue-50"}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <p className="font-medium text-blue-400">CMS iQIES Submission Requirements</p>
              <div className={`text-sm mt-2 space-y-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                <p>• HOPE assessments must be submitted within 30 days of completion</p>
                <p>• XML format required for CMS iQIES portal upload</p>
                <p>• All required fields must be validated before submission</p>
                <p>• Submission deadline: 15th of each month for prior month data</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-6">
        {/* Export Controls */}
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-emerald-400" />
              Export HOPE Data to iQIES
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs mb-2 block">Export Format</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="xml">XML (CMS Required)</SelectItem>
                  <SelectItem value="json">JSON (Backup)</SelectItem>
                  <SelectItem value="csv">CSV (Review)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button 
                onClick={validateBatch} 
                disabled={validating}
                variant="outline"
                className="flex-1"
              >
                {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Validate Batch
              </Button>
              <Button 
                onClick={exportBatch} 
                disabled={exporting || !validationResult?.validation_passed}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Generate Export
              </Button>
            </div>

            {validationResult && (
              <div className={`p-3 rounded-lg ${validationResult.validation_passed ? "bg-green-500/10 border border-green-500/30" : "bg-red-500/10 border border-red-500/30"}`}>
                <div className="flex items-center gap-2 mb-2">
                  {validationResult.validation_passed ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-400" />
                  )}
                  <span className={`text-sm font-medium ${validationResult.validation_passed ? "text-green-400" : "text-red-400"}`}>
                    {validationResult.validation_passed ? "Validation Passed" : "Validation Failed"}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center">
                    <p className="text-green-400 font-bold">{validationResult.valid_count}</p>
                    <p className={isDark ? "text-gray-500" : "text-gray-600"}>Valid</p>
                  </div>
                  <div className="text-center">
                    <p className="text-yellow-400 font-bold">{validationResult.warning_count}</p>
                    <p className={isDark ? "text-gray-500" : "text-gray-600"}>Warnings</p>
                  </div>
                  <div className="text-center">
                    <p className="text-red-400 font-bold">{validationResult.error_count}</p>
                    <p className={isDark ? "text-gray-500" : "text-gray-600"}>Errors</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Last Export Info */}
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-400" />
              Last Export
            </CardTitle>
          </CardHeader>
          <CardContent>
            {exportStatus?.last_export ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Batch ID</span>
                  <Badge variant="outline">{exportStatus.last_export.batch_id}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Date</span>
                  <span className="text-sm">
                    {exportStatus.last_export.date || exportStatus.last_export.created_at 
                      ? new Date(exportStatus.last_export.date || exportStatus.last_export.created_at).toLocaleDateString()
                      : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Records</span>
                  <span className="text-sm font-medium">{exportStatus.last_export.record_count}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Status</span>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                    {exportStatus.last_export.status}
                  </Badge>
                </div>
              </div>
            ) : (
              <p className={`text-sm ${isDark ? "text-gray-500" : "text-gray-600"}`}>No previous exports</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Records Ready for Export */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-blue-400" />
            Records Ready for Export ({recordsForExport.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recordsForExport.length > 0 ? (
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {recordsForExport.map((record) => (
                  <div 
                    key={record.id}
                    className={`p-3 rounded-lg flex items-center justify-between ${isDark ? "bg-slate-800" : "bg-gray-50"}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        record.validation_status === "valid" ? "bg-green-400" :
                        record.validation_status === "warning" ? "bg-yellow-400" : "bg-red-400"
                      }`} />
                      <div>
                        <p className="font-medium text-sm">{record.patient_name}</p>
                        <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-600"}`}>
                          {record.record_type} • {new Date(record.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {record.record_type}
                      </Badge>
                      <Badge className={
                        record.validation_status === "valid" ? "bg-green-500/20 text-green-400" :
                        record.validation_status === "warning" ? "bg-yellow-500/20 text-yellow-400" :
                        "bg-red-500/20 text-red-400"
                      }>
                        {record.validation_status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400/50" />
              <p className={`text-sm ${isDark ? "text-gray-500" : "text-gray-600"}`}>
                All HOPE records have been exported
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export Download Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className={`max-w-2xl ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-400" />
              iQIES Export Generated
            </DialogTitle>
            <DialogDescription>
              Your HOPE data export is ready. Download the file and upload it to the CMS iQIES portal.
            </DialogDescription>
          </DialogHeader>
          
          {exportedContent && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className={isDark ? "text-gray-500" : "text-gray-600"}>Batch ID</p>
                    <p className="font-mono">{exportedContent.batch_id}</p>
                  </div>
                  <div>
                    <p className={isDark ? "text-gray-500" : "text-gray-600"}>Format</p>
                    <p>{exportedContent.format}</p>
                  </div>
                  <div>
                    <p className={isDark ? "text-gray-500" : "text-gray-600"}>Records</p>
                    <p>{exportedContent.record_count}</p>
                  </div>
                  <div>
                    <p className={isDark ? "text-gray-500" : "text-gray-600"}>Filename</p>
                    <p className="font-mono text-xs">{exportedContent.filename}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Close
            </Button>
            <Button onClick={downloadExport} className="bg-emerald-600 hover:bg-emerald-700">
              <FileText className="w-4 h-4 mr-2" />
              Download File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TrackingSubTab({ isDark, submissionTracking }) {
  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4">
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{submissionTracking?.stats?.accepted || 0}</p>
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Accepted</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-400">{submissionTracking?.stats?.rejected || 0}</p>
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Rejected</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-400">{submissionTracking?.stats?.pending || 0}</p>
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Pending</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-400">{submissionTracking?.stats?.acceptance_rate || 0}%</p>
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Acceptance Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Submission History */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-purple-400" />
            Submission History & Status Workflow
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-96">
            <div className="space-y-4">
              {submissionTracking?.submissions?.map((submission, idx) => (
                <div key={idx} className={`p-4 rounded-lg border ${isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200"}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="font-mono">{submission.batch_id}</Badge>
                      <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                        {submission.record_count} records
                      </span>
                    </div>
                    <Badge className={
                      submission.status === "accepted" ? "bg-green-500/20 text-green-400 border-green-500/30" :
                      submission.status === "rejected" ? "bg-red-500/20 text-red-400 border-red-500/30" :
                      "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                    }>
                      {submission.status}
                    </Badge>
                  </div>
                  
                  {/* Status Workflow Timeline */}
                  {submission.status_history && (
                    <div className="flex items-center gap-2 mb-3">
                      {submission.status_history.map((step, stepIdx) => (
                        <div key={stepIdx} className="flex items-center">
                          <div className={`px-2 py-1 rounded text-xs ${
                            step.status === "accepted" ? "bg-green-500/20 text-green-400" :
                            step.status === "rejected" ? "bg-red-500/20 text-red-400" :
                            "bg-slate-700 text-slate-300"
                          }`}>
                            {step.status}
                          </div>
                          {stepIdx < submission.status_history.length - 1 && (
                            <ChevronRight className="w-4 h-4 text-slate-600 mx-1" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <p className={isDark ? "text-gray-500" : "text-gray-600"}>Created</p>
                      <p>{new Date(submission.created_at).toLocaleString()}</p>
                    </div>
                    {submission.submitted_at && (
                      <div>
                        <p className={isDark ? "text-gray-500" : "text-gray-600"}>Submitted</p>
                        <p>{new Date(submission.submitted_at).toLocaleString()}</p>
                      </div>
                    )}
                    {submission.cms_response_at && (
                      <div>
                        <p className={isDark ? "text-gray-500" : "text-gray-600"}>CMS Response</p>
                        <p>{new Date(submission.cms_response_at).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                  
                  {submission.cms_confirmation && (
                    <div className="mt-2 p-2 bg-green-500/10 rounded border border-green-500/20">
                      <p className="text-xs text-green-400">
                        CMS Confirmation: <span className="font-mono">{submission.cms_confirmation}</span>
                      </p>
                    </div>
                  )}
                  
                  {submission.rejection_reason && (
                    <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/20">
                      <p className="text-xs text-red-400">
                        Rejection Reason: {submission.rejection_reason}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}

function SchedulerSubTab({ isDark, schedulerConfig, runningScheduler, runSchedulerNow }) {
  return (
    <div className="space-y-6">
      {/* Scheduler Status */}
      <div className="grid grid-cols-3 gap-4">
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Scheduler Status</span>
              <Badge className={schedulerConfig?.config?.enabled ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                {schedulerConfig?.config?.enabled ? "Enabled" : "Disabled"}
              </Badge>
            </div>
            <p className="text-lg font-bold">{schedulerConfig?.config?.days_before_deadline || 5} Days</p>
            <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-600"}`}>Before deadline reminder</p>
          </CardContent>
        </Card>
        
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-blue-400" />
              <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Next Run</span>
            </div>
            <p className="text-lg font-bold">
              {schedulerConfig?.next_scheduled_run ? new Date(schedulerConfig.next_scheduled_run).toLocaleString() : "Not scheduled"}
            </p>
          </CardContent>
        </Card>
        
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-purple-400" />
              <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Upcoming Deadline</span>
            </div>
            <p className="text-lg font-bold text-purple-400">
              {schedulerConfig?.upcoming_deadline?.days_remaining || 0} Days
            </p>
            <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-600"}`}>
              {schedulerConfig?.upcoming_deadline?.records_pending || 0} records pending
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Scheduler Configuration */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Brain className="w-4 h-4 text-emerald-400" />
            Automated Scheduler Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Auto-Validate</span>
                <Badge className={schedulerConfig?.config?.auto_validate ? "bg-green-500/20 text-green-400" : "bg-slate-700"}>
                  {schedulerConfig?.config?.auto_validate ? "On" : "Off"}
                </Badge>
              </div>
              <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-600"}`}>
                Automatically validate records before deadline
              </p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Auto-Export</span>
                <Badge className={schedulerConfig?.config?.auto_export ? "bg-green-500/20 text-green-400" : "bg-slate-700"}>
                  {schedulerConfig?.config?.auto_export ? "On" : "Off"}
                </Badge>
              </div>
              <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-600"}`}>
                Generate export file automatically
              </p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Staff Notifications</span>
                <Badge className={schedulerConfig?.config?.notify_staff ? "bg-green-500/20 text-green-400" : "bg-slate-700"}>
                  {schedulerConfig?.config?.notify_staff ? "On" : "Off"}
                </Badge>
              </div>
              <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-600"}`}>
                Email staff when deadline approaches
              </p>
            </div>
          </div>

          <Button 
            onClick={runSchedulerNow} 
            disabled={runningScheduler}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            {runningScheduler ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
            Run Scheduler Now
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function WebhookSubTab({ 
  isDark, webhookConfig, webhookEvents, testingWebhook, 
  selectedTestEvent, setSelectedTestEvent, testWebhook, copyWebhookUrl 
}) {
  return (
    <div className="space-y-6">
      {/* Webhook Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-400">{webhookEvents?.length || 0}</p>
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Total Events</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-400">
              {webhookEvents?.filter(e => new Date(e.received_at).toDateString() === new Date().toDateString()).length || 0}
            </p>
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Events Today</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4 text-center">
            <Badge className={webhookConfig?.enabled ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
              {webhookConfig?.enabled ? "Active" : "Inactive"}
            </Badge>
            <p className={`text-sm mt-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>Webhook Status</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4 text-center">
            <p className="text-sm font-mono text-purple-400 truncate">
              {webhookEvents?.[0]?.received_at ? new Date(webhookEvents[0].received_at).toLocaleString() : "N/A"}
            </p>
            <p className={`text-sm mt-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>Last Event</p>
          </CardContent>
        </Card>
      </div>

      {/* Webhook URL */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-purple-400" />
            CMS Webhook Endpoint
          </CardTitle>
          <CardDescription>Configure this URL in the CMS iQIES portal for automatic status updates</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Input 
              value={webhookConfig?.webhook_url || ""} 
              readOnly 
              className={`font-mono text-xs ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
            />
            <Button variant="outline" size="sm" onClick={copyWebhookUrl}>
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Supported Events */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm">Supported Webhook Events</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {WEBHOOK_EVENTS.map(event => (
              <div key={event.value} className={`p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                <p className="font-medium text-sm">{event.label}</p>
                <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-600"}`}>{event.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Test Webhook */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <PlayCircle className="w-4 h-4 text-emerald-400" />
            Test Webhook Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Select value={selectedTestEvent} onValueChange={setSelectedTestEvent}>
              <SelectTrigger className={`w-64 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEBHOOK_EVENTS.map(event => (
                  <SelectItem key={event.value} value={event.value}>{event.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={testWebhook} disabled={testingWebhook}>
              {testingWebhook ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
              Send Test Event
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Webhook Events */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-400" />
            Recent Webhook Events
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64">
            {webhookEvents?.length > 0 ? (
              <div className="space-y-2">
                {webhookEvents.map((event, idx) => (
                  <div key={idx} className={`p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline">{event.event_type}</Badge>
                      <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-600"}`}>
                        {new Date(event.received_at).toLocaleString()}
                      </span>
                    </div>
                    {event.actions_taken && (
                      <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                        Actions: {event.actions_taken.join(", ")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-slate-500/50" />
                <p className={`text-sm ${isDark ? "text-gray-500" : "text-gray-600"}`}>
                  No webhook events received yet
                </p>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
